// src/hooks/useAnalytics.js - Custom hook for analytics tracking
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  trackPageView, 
  trackNavigation,
  trackScrollDepth,
  trackEvent,
  isGAReady 
} from '../utils/analytics';

// Main analytics hook for page tracking
export const useAnalytics = () => {
  const location = useLocation();
  const previousLocation = useRef();

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const pageTitle = document.title;
    
    console.log('📊 [ANALYTICS] Tracking page view:', { currentPath, pageTitle }); // DEBUG LOG
    
    // Track page view
    trackPageView(currentPath, pageTitle, {
      page_referrer: document.referrer,
      page_search: location.search,
      page_hash: location.hash
    });

    // Track navigation (if coming from another page)
    if (previousLocation.current) {
      console.log('📊 [ANALYTICS] Tracking navigation:', {
        from: previousLocation.current.pathname,
        to: location.pathname
      }); // DEBUG LOG
      
      trackNavigation(
        previousLocation.current.pathname,
        location.pathname,
        'route_change'
      );
    }

    // Update previous location
    previousLocation.current = location;
  }, [location]);

  return {
    trackPageView,
    trackNavigation,
    trackEvent,
    isReady: isGAReady()
  };
};

// Hook for scroll depth tracking (useful for blog articles)
export const useScrollTracking = (threshold = [25, 50, 75, 90, 100]) => {
  const trackedDepths = useRef(new Set());
  const location = useLocation();

  useEffect(() => {
    // Reset tracked depths on page change
    trackedDepths.current.clear();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const scrollPercent = Math.round((scrolled / scrollHeight) * 100);

      // Track each threshold once
      threshold.forEach(depth => {
        if (scrollPercent >= depth && !trackedDepths.current.has(depth)) {
          trackedDepths.current.add(depth);
          trackScrollDepth(depth, location.pathname);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, threshold]);
};

// Hook for tracking form interactions
export const useFormAnalytics = (formName) => {
  const trackFormStart = () => {
    trackEvent('form_start', {
      form_name: formName,
      form_location: window.location.pathname
    });
  };

  const trackFormSubmit = (success = true, errorMessage = '') => {
    trackEvent('form_submit', {
      form_name: formName,
      form_location: window.location.pathname,
      success: success,
      error_message: errorMessage
    });
  };

  const trackFormAbandonment = () => {
    trackEvent('form_abandonment', {
      form_name: formName,
      form_location: window.location.pathname
    });
  };

  return {
    trackFormStart,
    trackFormSubmit,
    trackFormAbandonment
  };
};

// Hook for tracking component visibility (intersection observer)
export const useVisibilityTracking = (componentName, threshold = 0.5) => {
  const elementRef = useRef();
  const hasBeenVisible = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || hasBeenVisible.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible.current) {
          hasBeenVisible.current = true;
          trackEvent('component_view', {
            component_name: componentName,
            page_location: window.location.pathname
          });
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [componentName, threshold]);

  return elementRef;
};

// Hook for tracking reading time (useful for blog articles)
export const useReadingTime = (contentLength = 0) => {
  const startTime = useRef();
  const location = useLocation();

  useEffect(() => {
    startTime.current = Date.now();

    return () => {
      if (startTime.current) {
        const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
        
        // Only track if user spent meaningful time (more than 5 seconds)
        if (timeSpent > 5) {
          trackEvent('reading_time', {
            time_spent: timeSpent,
            content_length: contentLength,
            page_location: location.pathname,
            words_per_minute: contentLength > 0 ? Math.round((contentLength / timeSpent) * 60) : 0
          });
        }
      }
    };
  }, [location.pathname, contentLength]);
};

// Hook for tracking clicks on specific elements
export const useClickTracking = (category = 'interaction') => {
  const trackClick = (elementName, additionalData = {}) => {
    trackEvent('click', {
      element_name: elementName,
      click_category: category,
      page_location: window.location.pathname,
      ...additionalData
    });
  };

  return { trackClick };
};